import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Icon,
  Stack,
  Chip,
  useTheme,
  Paper,
  alpha,
  styled,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import { POSSellableItem } from "../../../services/pos-sellable-items.service";
import { IESDoc } from "../../../../../app.interface";
import { styled as muiStyled } from "@mui/material/styles";

interface Recipe {
  name: string;
  recipe_id: string;
  quantity?: number;
  unit?: string;
}

interface POSSellableItemDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  item: IESDoc<POSSellableItem>;
}

// Custom theme colors to match the form
const THEME_COLORS = {
  primary: '#0891B2',
  primaryHover: '#0E7490',
  secondary: '#64748B',
  danger: '#DC2626',
  dangerHover: '#B91C1C',
  success: '#059669',
  background: {
    main: '#FFFFFF',
    light: '#F8FAFC',
    card: '#FFFFFF',
  },
  border: {
    light: alpha('#0891B2', 0.1),
    main: alpha('#0891B2', 0.2),
  },
  text: {
    primary: '#1E293B',
    secondary: '#64748B',
    light: '#94A3B8',
  }
};

const StyledCard = muiStyled(Card)(({ theme }) => ({
  backgroundColor: THEME_COLORS.background.card,
  borderRadius: 12,
  border: `1px solid ${THEME_COLORS.border.light}`,
  marginBottom: theme.spacing(3),
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
}));

const SectionTitle = muiStyled(Typography)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: THEME_COLORS.primary,
  fontFamily: 'Montserrat',
  fontWeight: 600,
  marginBottom: 16,
  '& .MuiIcon-root': {
    color: THEME_COLORS.primary,
    fontSize: 20,
  },
});

const EmptyState = ({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        px: 2,
        textAlign: "center",
        bgcolor: alpha(THEME_COLORS.primary, 0.04),
        borderRadius: 2,
      }}
    >
      <Icon
        sx={{
          fontSize: 48,
          color: THEME_COLORS.primary,
          opacity: 0.5,
          mb: 2,
        }}
      >
        {icon}
      </Icon>
      <Typography 
        variant="h6" 
        gutterBottom
        sx={{ 
          color: THEME_COLORS.text.primary,
          fontFamily: 'Montserrat',
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ color: THEME_COLORS.text.secondary }}
      >
        {description}
      </Typography>
    </Box>
  );
};

export const POSSellableItemDetailsDrawer: React.FC<
  POSSellableItemDetailsDrawerProps
> = ({ open, onClose, item }) => {
  const theme = useTheme();

  // Calculate costs
  const rawMaterialsCost = (item.ingredients || []).reduce((sum: number, ing: any) => {
    if (Array.isArray(ing.selected_batches) && ing.selected_batches.length > 0) {
      return sum + ing.selected_batches.reduce((bSum: number, b: any) => bSum + (b.quantity * b.cost_per_unit), 0);
    }
    return sum;
  }, 0);
  const processedItemsCost = (item.processed_items || []).reduce((sum: number, pi: any) => sum + (pi.total_cost || 0), 0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "600px",
          maxWidth: "100%",
          bgcolor: THEME_COLORS.background.light,
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${THEME_COLORS.border.light}`,
            bgcolor: THEME_COLORS.background.main,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon sx={{ color: THEME_COLORS.primary }}>inventory</Icon>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  color: THEME_COLORS.text.primary,
                }}
              >
                {item.name}
              </Typography>
            </Box>
            <IconButton 
              onClick={onClose}
              sx={{ 
                color: THEME_COLORS.text.secondary,
                '&:hover': {
                  bgcolor: alpha(THEME_COLORS.primary, 0.04),
                }
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>
            {/* Basic Info */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>info</Icon>
                Basic Information
              </SectionTitle>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: THEME_COLORS.text.secondary,
                      mb: 1,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: THEME_COLORS.text.primary,
                      fontWeight: 500,
                    }}
                  >
                    {item.description || "No description provided"}
                  </Typography>
                </Grid>
                {/* <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: THEME_COLORS.text.secondary,
                      mb: 1,
                    }}
                  >
                    Price
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: THEME_COLORS.text.primary,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Icon sx={{ color: THEME_COLORS.primary, fontSize: 20 }}>attach_money</Icon>
                    {item.price?.toFixed(3)} {item.currency}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Total Cost
                  </Typography>
                  <Typography variant="body1">
                    {item.total_cost?.toFixed(3)} {item.currency}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Preparation Time
                  </Typography>
                  <Typography variant="body1">
                    {item.preparation_time} minutes
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Calories
                  </Typography>
                  <Typography variant="body1">
                    {item.calories || "Not specified"}
                  </Typography>
                </Grid> */}
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Created At
                  </Typography>
                  <Typography variant="body1" sx={{ color: THEME_COLORS.text.primary, fontWeight: 500 }}>
                    {dayjs(item.created_at).format("MMM D, YYYY h:mm A")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Status
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip
                      size="small"
                      icon={<Icon sx={{ fontSize: 16, color: '#059669' }}>check_circle</Icon>}
                      label="Available"
                      sx={{
                        bgcolor: '#E6F4EF',
                        color: '#059669',
                        border: '1px solid #B7E2D2',
                        fontWeight: 600,
                        borderRadius: 1,
                        fontSize: '0.95em',
                        px: 1.5,
                        '& .MuiChip-icon, & .MuiChip-icon svg': {
                          color: '#059669 !important',
                        },
                      }}
                      variant="outlined"
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Cost Breakdown */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>calculate</Icon>
                Cost Breakdown
              </SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>Total Cost</Typography>
                  <Typography variant="h6" sx={{ color: THEME_COLORS.primary, fontWeight: 700 }}>{item.total_cost?.toFixed(3)} {item.currency}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>Raw Materials</Typography>
                  <Typography variant="body1" sx={{ color: THEME_COLORS.text.primary, fontWeight: 500 }}>{rawMaterialsCost.toFixed(3)} {item.currency}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>Processed Items</Typography>
                  <Typography variant="body1" sx={{ color: THEME_COLORS.text.primary, fontWeight: 500 }}>{processedItemsCost.toFixed(3)} {item.currency}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>Total Items</Typography>
                  <Typography variant="body1" sx={{ color: THEME_COLORS.text.primary, fontWeight: 500 }}>{((item.ingredients?.length || 0) + (item.processed_items?.length || 0))}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>

          {/* Ingredients */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>restaurant</Icon>
                Ingredients
              </SectionTitle>
              {item.ingredients && item.ingredients.length > 0 ? (
                <List disablePadding sx={{ bgcolor: THEME_COLORS.background.light, borderRadius: 2, border: `1px solid ${THEME_COLORS.border.light}` }}>
                  {item.ingredients.map((ingredient: any, index: number) => (
                    <ListItem key={ingredient.ingredient_id || index} sx={{ px: 2, py: 1.5, borderBottom: index < item.ingredients.length - 1 ? `1px solid ${THEME_COLORS.border.light}` : 'none' }}>
                      <ListItemIcon>
                        <Icon sx={{ color: THEME_COLORS.primary }}>inventory_2</Icon>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ color: THEME_COLORS.text.primary, fontWeight: 500 }}>{ingredient.brand_name || ingredient.name}</Typography>}
                        secondary={<Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>{ingredient.required_quantity || ingredient.quantity} {ingredient.unit}</Typography>}
                      />
                      {ingredient.notes && (
                        <Chip size="small" icon={<Icon fontSize="small">notes</Icon>} label="Has notes" sx={{ bgcolor: alpha(THEME_COLORS.primary, 0.1), color: THEME_COLORS.primary, borderRadius: 1 }} />
                      )}
                      {/* Batch info */}
                      {ingredient.selected_batches && ingredient.selected_batches.length > 0 && (
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">Selected Batches</Typography>
                          <Stack spacing={1}>
                            {ingredient.selected_batches.map((batch: any) => (
                              <Box key={batch._id} sx={{ p: 1, borderRadius: 1, bgcolor: alpha(THEME_COLORS.primary, 0.04), border: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}` }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>#{batch._id?.slice(-6)} • {batch.quantity} {batch.unit}</Typography>
                                    <Typography variant="caption" color="text.secondary">{batch.cost_per_unit?.toFixed(3)} {item.currency}/{batch.unit}</Typography>
                                  </Box>
                                  <Typography variant="body2" color="primary" fontWeight={500}>{(batch.quantity * batch.cost_per_unit)?.toFixed(3)} {item.currency}</Typography>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <EmptyState icon="restaurant" title="No Ingredients" description="No ingredients have been added to this item yet" />
              )}
            </CardContent>
          </StyledCard>

          {/* Processed Items */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle variant="subtitle1">
                <Icon>inventory</Icon>
                Processed Items
              </SectionTitle>
              {item.processed_items && item.processed_items.length > 0 ? (
                <List disablePadding sx={{ bgcolor: THEME_COLORS.background.light, borderRadius: 2, border: `1px solid ${THEME_COLORS.border.light}` }}>
                  {item.processed_items.map((proc: any, index: number) => (
                    <ListItem key={proc.processed_item_id || index} sx={{ px: 2, py: 1.5, borderBottom: index < item.processed_items.length - 1 ? `1px solid ${THEME_COLORS.border.light}` : 'none' }}>
                      <ListItemIcon>
                        <Icon sx={{ color: THEME_COLORS.primary }}>inventory</Icon>
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ color: THEME_COLORS.text.primary, fontWeight: 500 }}>{proc.name}</Typography>}
                        secondary={<Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>{proc.required_quantity || proc.quantity} {proc.unit}</Typography>}
                      />
                      {/* Cost info */}
                      {proc.total_cost !== undefined && (
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">Total Cost</Typography>
                          <Typography variant="body2" color="primary" fontWeight={500}>{proc.total_cost?.toFixed(3)} {item.currency}</Typography>
                          {proc.cost_per_unit && (
                            <Typography variant="caption" color="text.secondary">{proc.cost_per_unit?.toFixed(3)} {item.currency}/{proc.unit}</Typography>
                          )}
                        </Box>
                      )}
                      {/* Batch info */}
                      {proc.selected_batches && proc.selected_batches.length > 0 && (
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">Selected Batches</Typography>
                          <Stack spacing={1}>
                            {proc.selected_batches.map((batch: any) => (
                              <Box key={batch._id} sx={{ p: 1, borderRadius: 1, bgcolor: alpha(THEME_COLORS.primary, 0.04), border: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}` }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>#{batch._id?.slice(-6)} • {batch.quantity} {batch.unit}</Typography>
                                    <Typography variant="caption" color="text.secondary">{batch.cost_per_unit?.toFixed(3)} {item.currency}/{batch.unit}</Typography>
                                  </Box>
                                  <Typography variant="body2" color="primary" fontWeight={500}>{(batch.quantity * batch.cost_per_unit)?.toFixed(3)} {item.currency}</Typography>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <EmptyState icon="inventory" title="No Processed Items" description="No processed items have been added to this item" />
              )}
            </CardContent>
          </StyledCard>
        </Box>
      </Box>
    </Drawer>
  );
};
